import dynamic from 'next/dynamic';
import React from 'react';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { Grid } from '@mui/material';
import Box from '@mui/material/Box';

import { i18n, useTranslation } from 'next-i18next';

import Typography from 'src/components/ui/Typography';
import ChatButton from 'src/components/ui/ChatButton';
import FeeScaleModal from 'src/components/profile/FeeScaleModal';
import GoogleMapWithMarker from 'src/components/map/GoogleMapWithMarker';
import { googleMapDefaultOptions } from 'src/components/map/GoogleMap';
import Icon from 'src/components/ui/Icon';
import Link from 'src/components/ui/Link';
import HiddenPhoneNumberButton from 'src/components/ui/HiddenPhoneNumberButton';
import HiddenEmailButton from 'src/components/ui/HiddenEmailButton';

import theme from 'src/utils/theme';
import { getCompanyTitleTKey } from 'src/utils/companyUtils';
import type {
    Company,
    User,
    PagesJaunesPro,
    PhoneNumber,
    PagesJaunesAddress,
    QuickSignupCompany,
    CompanyCapatibilities,
} from 'src/types/__generated__/graphql';
import { colours } from 'src/constants/colours';
import { breakpoints } from 'src/constants/breakpoints';
import useUser from 'src/hooks/useUser';
import { formatSiret } from 'src/utils/formatSiret';
import { routePathCompany } from 'src/constants/router';
import useEvents from 'src/hooks/useEvents';
import useModalOpenState from 'src/hooks/useModalOpenState';
import { styledTag, styledComponent } from 'src/utils/styled';
import useRemoteConfig from 'src/hooks/useRemoteConfig';
import PagesJaunesOwnershipSection from 'src/components/company/PagesJaunesOwnershipSection';
import useBreakpoints from 'src/hooks/useBreakpoints';

const PagesJaunesLogo = dynamic(() => import('src/assets/logos/logo_pagesjaune-with-text.svg'), {
    ssr: false,
});

const StyledCard = styledComponent(Card)`
    position: sticky;
    top: 1.5rem;
`;
StyledCard.defaultProps = {
    variant: 'outlined',
};

const StyledCardContent = styledComponent(CardContent)`
    padding: 1.5rem;
    &.MuiCardContent-root:last-child {
        padding-bottom: 1.5rem;
    }
`;

const ExternalLink = styledTag('a')`
    font-size: 0.875rem;
    color: ${theme.palette.secondary.main};
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 66%;
`;

const DarkLink = styledComponent(Link)`
    font-size: 0.875rem;
    color: ${colours.black.O87};
    font-weight: bold;
`;

const DarkerText = styledComponent(Typography)`
    color: ${colours.black.O87};
`;

const MapGrid = styledComponent(Grid)`
    height: 30rem;
    margin: 1rem -1rem;

    @media (min-width: ${breakpoints.tabletPortrait}px) {
        margin: 1rem 0;
    }
`;

const StyledLink = styledComponent(Link)`
     display:flex;
     align-items: center;
     text-decoration:none;
`;

export type PagesJaunesProDetailsProps = Pick<
    PagesJaunesPro,
    | 'name'
    | 'website'
    | 'address'
    | 'description'
    | 'phoneNumber'
    | 'email'
    | 'proId'
    | 'activityLabel'
    | 'foundationYear'
    | 'siret'
    | 'pageUrl'
    | 'canBeClaimedByCurrentUser'
    | 'pagesJaunesProType'
>;

export type CompanyDetailsProps = Pick<
    Company,
    | 'id'
    | 'website'
    | 'phoneNumber'
    | 'address'
    | 'foundationYear'
    | 'siretNumber'
    | 'feeScaleFile'
    | 'feeScaleFileName'
    | 'contactUser'
    | 'capabilities'
    | 'website'
    | 'pagesJaunesUrl'
> & { name: Company['name'] | undefined };

interface Props {
    user?: Partial<Pick<User, 'feeScaleFile' | 'feeScaleFileName' | 'phoneNumber'>>;
    company?: CompanyDetailsProps;
    pagesJaunesPro?: PagesJaunesProDetailsProps;
    quickSignupCompany?: QuickSignupCompany;
    displayOnCard?: boolean;
    openPagesJaunesClaimModal?: () => void;
}

/**
 * Items that are common for Company and for PagesJaunesPro (and eventually for QuickSignupCompany)
 */
type CommonCompanyDetails = {
    name?: string;
    linkHref?: string;
    linkIsExternal?: boolean;
    linkLabel?: string;
    websiteLinkHref?: string;
    websiteLinkIsExternal?: boolean;
    websiteLinkLabel?: string;
    capabilites?: CompanyCapatibilities;
    gmapLinkQ?: string;
    addressDescription?: string;
    foundationYear?: string;
    siretNumber?: string;
    phoneNumber?: PhoneNumber;
    activityLabel?: string;
    email?: string;
    /**
     * min width (in %) of the field label.
     * For example, `30` for setting the minWidth to `30%`
     */
    labelMinWidth: {
        isMobile: number;
        notIsMobile: number;
    };
    pagesJaunesUrl?: string;
};

const minWithByDefaultIsMobile = 30;
const minWithByDefaultNotIsMobile = 40;

const getCommonCompanyDetails = (props: Props): CommonCompanyDetails | undefined => {
    if (props.company) {
        return {
            name: props.company.name,
            linkHref: props.company.capabilities?.hasPublicPage ? `${routePathCompany}/${props.company.id}` : undefined,
            linkIsExternal: false,
            websiteLinkHref: props.company.website ?? undefined,
            websiteLinkIsExternal: true,
            websiteLinkLabel: props.company.website ?? undefined,
            capabilities: props.company.capabilities,
            linkLabel: props.company.name,
            gmapLinkQ: props.company.address?.description,
            addressDescription: props.company.address?.description,
            foundationYear: String(props.company.foundationYear ?? ''),
            siretNumber: props.company.siretNumber ?? undefined,
            phoneNumber: props.company.phoneNumber ?? undefined,
            labelMinWidth: {
                isMobile: minWithByDefaultIsMobile,
                notIsMobile: minWithByDefaultNotIsMobile,
            },
            pagesJaunesUrl: props.company.pagesJaunesUrl,
        } as CommonCompanyDetails;
    }

    if (props.pagesJaunesPro) {
        const address: PagesJaunesAddress | undefined =
            props.pagesJaunesPro.address !== null ? props.pagesJaunesPro.address : undefined;
        const addressDescription: string | undefined =
            address?.addressDescription !== null && address?.addressDescription !== undefined
                ? (address.addressDescription as string)
                : undefined;
        return {
            name: props.pagesJaunesPro.name,
            websiteLinkHref: props.pagesJaunesPro.website?.url ?? undefined,
            websiteLinkIsExternal: true,
            websiteLinkLabel: props.pagesJaunesPro.website?.label ?? undefined,
            pageUrl: props.pagesJaunesPro.pageUrl,
            addressDescription,
            gmapLinkQ: addressDescription,
            foundationYear: props.pagesJaunesPro.foundationYear ?? undefined,
            siretNumber: props.pagesJaunesPro.siret ?? undefined,
            phoneNumber: props.pagesJaunesPro.phoneNumber ?? undefined,
            activityLabel:
                props.pagesJaunesPro.activityLabel &&
                i18n?.exists(`common:activities.${props.pagesJaunesPro.activityLabel}`)
                    ? `common:activities.${props.pagesJaunesPro.activityLabel}`
                    : undefined,
            email: props.pagesJaunesPro.email ?? undefined,
            labelMinWidth: {
                isMobile: 50,
                notIsMobile: 50,
            },
        } as CommonCompanyDetails;
    }

    if (props.quickSignupCompany) {
        return {
            name: props.quickSignupCompany.name,
            websiteLinkHref: props.quickSignupCompany.website ?? undefined,
            websiteLinkIsExternal: true,
            websiteLinkLabel: props.quickSignupCompany.website ?? undefined,
            gmapLinkQ: props.quickSignupCompany.address?.description ?? undefined,
            addressDescription: props.quickSignupCompany.address?.description ?? undefined,
            siretNumber: props.quickSignupCompany.siretNumber ?? undefined,
            phoneNumber: props.quickSignupCompany.phone ?? undefined,
            activityLabel:
                props.quickSignupCompany.activity?.labelKey &&
                i18n?.exists(`common:activities.${props.quickSignupCompany.activity.labelKey}`)
                    ? `common:activities.${props.quickSignupCompany.activity.labelKey}`
                    : undefined,
            email: props.quickSignupCompany.email ?? undefined,
            labelMinWidth: {
                isMobile: 50,
                notIsMobile: 50,
            },
        };
    }

    return undefined;
};

const CompanyDetailsBlock: React.FC<Props> = (props) => {
    const {
        user,
        company,
        pagesJaunesPro,
        quickSignupCompany,
        openPagesJaunesClaimModal,
        displayOnCard = false,
    } = props;
    const { t } = useTranslation(['profile', 'common', 'company']);
    const currentUser = useUser();
    const { trackClickWebsitePagesJaunesMerchant, trackRedirectToPagesJaunesMerchantPage } = useEvents();
    const { props: feeScaleModalProps, open: openFeeScaleModal } = useModalOpenState();
    const { showPagesJaunesOwnershipSection } = useRemoteConfig();

    const isCompany = company && !pagesJaunesPro && !quickSignupCompany;
    const isPagesJaunesPro = Boolean(pagesJaunesPro);

    const commonCompanyDetails = getCommonCompanyDetails(props);

    const { isMobile } = useBreakpoints();

    const gmapLink = commonCompanyDetails?.gmapLinkQ
        ? `https://maps.google.com/?q=${commonCompanyDetails.gmapLinkQ}`
        : undefined;

    const handleClickWebsite = (e: React.MouseEvent) => {
        if (props.pagesJaunesPro) {
            trackClickWebsitePagesJaunesMerchant({
                pagesJaunesMerchantId: props.pagesJaunesPro.proId,
                userId: currentUser?.id,
            });
        }
    };

    const handleClickPagesJaunesLogo = (e: React.MouseEvent) => {
        if (props.pagesJaunesPro) {
            trackRedirectToPagesJaunesMerchantPage({
                pagesJaunesMerchantId: props.pagesJaunesPro.proId,
                userId: currentUser?.id,
            });
        }
    };

    const companyLabelTKey = getCompanyTitleTKey(commonCompanyDetails as unknown as Company);
    const phoneNumber = commonCompanyDetails?.phoneNumber ?? user?.phoneNumber;

    // Don't show PagesJaunesOwnershipSection if feature flag is false or if user is connected and is not a professional
    const displayPagesJaunesOwnershipSection =
        showPagesJaunesOwnershipSection && (!currentUser || currentUser.isProfessionnal);

    const content = (
        <>
            {displayOnCard && <Typography variant="h3">{t(companyLabelTKey)}</Typography>}
            {commonCompanyDetails?.name && (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent={displayOnCard ? 'space-between' : 'flex-start'}
                    my={displayOnCard ? 2 : 1.5}
                    data-target-id="CompanyDetails-name"
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        mr={2}
                        minWidth={`${
                            isMobile
                                ? commonCompanyDetails.labelMinWidth.isMobile
                                : commonCompanyDetails.labelMinWidth.notIsMobile
                        }%`}
                    >
                        <Box display="flex" alignItems="center" mr={1}>
                            <Icon name="Home" />
                        </Box>
                        <Typography color="dark">{t(companyLabelTKey)}</Typography>
                    </Box>
                    {commonCompanyDetails.linkHref && !commonCompanyDetails.linkIsExternal ? (
                        <Typography noWrap>
                            <DarkLink href={commonCompanyDetails.linkHref}>{commonCompanyDetails.linkLabel}</DarkLink>
                        </Typography>
                    ) : (
                        <Typography noWrap>{commonCompanyDetails.name}</Typography>
                    )}
                </Box>
            )}
            {commonCompanyDetails?.activityLabel && (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent={displayOnCard ? 'space-between' : 'flex-start'}
                    my={displayOnCard ? 2 : 1.5}
                    data-target-id="CompanyDetails-activityLabel"
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        mr={2}
                        minWidth={`${
                            isMobile
                                ? commonCompanyDetails.labelMinWidth.isMobile
                                : commonCompanyDetails.labelMinWidth.notIsMobile
                        }%`}
                    >
                        <Box display="flex" alignItems="center" mr={1}>
                            <Icon name="Inactive" />
                        </Box>
                        <Typography color="dark">{t('company:activity')}</Typography>
                    </Box>
                    {isCompany ? commonCompanyDetails.activityLabel : t(commonCompanyDetails.activityLabel)}
                </Box>
            )}
            {commonCompanyDetails?.websiteLinkHref && commonCompanyDetails.websiteLinkIsExternal && (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent={displayOnCard ? 'space-between' : 'flex-start'}
                    my={displayOnCard ? 2 : 1.5}
                    data-target-id="CompanyDetails-link"
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        mr={2}
                        minWidth={`${
                            isMobile
                                ? commonCompanyDetails.labelMinWidth.isMobile
                                : commonCompanyDetails.labelMinWidth.notIsMobile
                        }%`}
                    >
                        <Box display="flex" alignItems="center" mr={1}>
                            <Icon name="Globe" />
                        </Box>
                        <Typography color="dark">{t('profile:website')}</Typography>
                    </Box>
                    <ExternalLink
                        href={commonCompanyDetails.websiteLinkHref}
                        target="_blank"
                        rel="noreferrer noopener"
                        onClick={handleClickWebsite}
                        data-target-id="company-details-website"
                    >
                        {commonCompanyDetails.websiteLinkLabel}
                    </ExternalLink>
                </Box>
            )}
            {commonCompanyDetails?.pagesJaunesUrl && (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent={displayOnCard ? 'space-between' : 'flex-start'}
                    my={displayOnCard ? 2 : 1.5}
                    data-target-id="CompanyDetails-pagesjaunes-url"
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        mr={2}
                        minWidth={`${
                            isMobile
                                ? commonCompanyDetails.labelMinWidth.isMobile
                                : commonCompanyDetails.labelMinWidth.notIsMobile
                        }%`}
                    >
                        <Box display="flex" alignItems="center" mr={1}>
                            <Icon name="PagesJaunes" />
                        </Box>
                        <Typography color="dark">{t('common:pagesJaunes')}</Typography>
                    </Box>
                    <ExternalLink
                        href={commonCompanyDetails.pagesJaunesUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        data-target-id="company-details-pagesjaunes-url"
                    >
                        {t('common:see')}
                    </ExternalLink>
                </Box>
            )}
            {phoneNumber && (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent={displayOnCard ? 'space-between' : 'flex-start'}
                    my={displayOnCard ? 2 : 1.5}
                    data-target-id="CompanyDetails-phone"
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        mr={2}
                        minWidth={`${
                            isMobile
                                ? commonCompanyDetails?.labelMinWidth.isMobile ?? minWithByDefaultIsMobile
                                : commonCompanyDetails?.labelMinWidth.notIsMobile ?? minWithByDefaultNotIsMobile
                        }%`}
                    >
                        <Box display="flex" alignItems="center" mr={1}>
                            <Icon name="Call" />
                        </Box>
                        <Typography color="dark">{t('profile:phone')}</Typography>
                    </Box>
                    <HiddenPhoneNumberButton phoneNumber={phoneNumber} variant="outlined" />
                </Box>
            )}
            {commonCompanyDetails?.email && (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent={displayOnCard ? 'space-between' : 'flex-start'}
                    my={displayOnCard ? 2 : 1.5}
                    data-target-id="CompanyDetails-email"
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        mr={2}
                        minWidth={`${
                            isMobile
                                ? commonCompanyDetails.labelMinWidth.isMobile
                                : commonCompanyDetails.labelMinWidth.notIsMobile
                        }%`}
                    >
                        <Box display="flex" alignItems="center" mr={1}>
                            <Icon name="Mail" />
                        </Box>
                        <Typography color="dark">{t('common:share.email')}</Typography>
                    </Box>
                    <HiddenEmailButton
                        email={commonCompanyDetails.email}
                        variant="outlined"
                        data-target-id="company-details-email"
                    >
                        {commonCompanyDetails.email}
                    </HiddenEmailButton>
                </Box>
            )}
            {commonCompanyDetails?.foundationYear && (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent={displayOnCard ? 'space-between' : 'flex-start'}
                    my={displayOnCard ? 2 : 1.5}
                    data-target-id="CompanyDetails-foundationYear"
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        mr={2}
                        minWidth={`${
                            isMobile
                                ? commonCompanyDetails.labelMinWidth.isMobile
                                : commonCompanyDetails.labelMinWidth.notIsMobile
                        }%`}
                    >
                        <Box display="flex" alignItems="center" mr={1}>
                            <Icon name="Date" />
                        </Box>
                        <Typography color="dark">{t('profile:foundationYear')}</Typography>
                    </Box>
                    <DarkerText variant="body2" color="dark">
                        {commonCompanyDetails.foundationYear}
                    </DarkerText>
                </Box>
            )}
            {commonCompanyDetails?.siretNumber && (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent={displayOnCard ? 'space-between' : 'flex-start'}
                    my={displayOnCard ? 2 : 1.5}
                    data-target-id="CompanyDetails-siretNumber"
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        mr={2}
                        minWidth={`${
                            isMobile
                                ? commonCompanyDetails.labelMinWidth.isMobile
                                : commonCompanyDetails.labelMinWidth.notIsMobile
                        }%`}
                    >
                        <Box display="flex" alignItems="center" mr={1}>
                            <Icon name="Info" />
                        </Box>
                        <Typography color="dark">{t('profile:siretNumber')}</Typography>
                    </Box>
                    <DarkerText variant="body2" color="dark">
                        {formatSiret(commonCompanyDetails.siretNumber)}
                    </DarkerText>
                </Box>
            )}
            {commonCompanyDetails?.addressDescription && (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent={displayOnCard ? 'space-between' : 'flex-start'}
                    my={displayOnCard ? 2 : 1.5}
                    data-target-id="CompanyDetails-addressDesc"
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        mr={2}
                        minWidth={`${
                            isMobile
                                ? commonCompanyDetails.labelMinWidth.isMobile
                                : commonCompanyDetails.labelMinWidth.notIsMobile
                        }%`}
                    >
                        <Box display="flex" alignItems="center" mr={1}>
                            <Icon size="1.66rem" name="Map" />
                        </Box>
                        <Typography color="dark">{t('profile:location')}</Typography>
                    </Box>
                    {gmapLink ? (
                        <ExternalLink href={gmapLink} target="_blank" rel="noreferrer noopener">
                            {commonCompanyDetails.addressDescription}
                        </ExternalLink>
                    ) : (
                        commonCompanyDetails.addressDescription
                    )}
                </Box>
            )}
            {isPagesJaunesPro && (
                <>
                    {displayPagesJaunesOwnershipSection && (
                        <PagesJaunesOwnershipSection
                            canBeClaimedByCurrentUser={pagesJaunesPro?.canBeClaimedByCurrentUser ?? false}
                            openPagesJaunesClaimModal={openPagesJaunesClaimModal}
                        />
                    )}
                    {pagesJaunesPro?.address?.lat && pagesJaunesPro.address.lng && (
                        <MapGrid>
                            <GoogleMapWithMarker
                                point={{
                                    lat: pagesJaunesPro.address.lat,
                                    lng: pagesJaunesPro.address.lng,
                                }}
                                options={{
                                    ...googleMapDefaultOptions,
                                    streetViewControl: false,
                                    fullscreenControl: false,
                                    mapTypeControl: false,
                                }}
                            />
                        </MapGrid>
                    )}
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        pt={1}
                        pb={2}
                        data-target-id="CompanyDetails-pagesJaunesIcon"
                    >
                        <StyledLink
                            onClick={handleClickPagesJaunesLogo}
                            href={String(pagesJaunesPro?.pageUrl)}
                            openInNewTab
                        >
                            <Typography variant="subtitle2" marginRight="0.35rem" marginBottom="0.2rem">
                                {t('company:inPartnershipWith')}
                            </Typography>
                            <PagesJaunesLogo height="27" />
                        </StyledLink>
                    </Box>
                </>
            )}
            {user?.feeScaleFile && user.feeScaleFileName ? (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent={displayOnCard ? 'space-between' : 'flex-start'}
                    my={displayOnCard ? 2 : 1.5}
                    data-target-id="CompanyDetails-feeScaleFile-user"
                >
                    <Box display="flex" alignItems="center" mr={2} minWidth={isMobile ? '30%' : '40%'}>
                        <Box display="flex" alignItems="center" mr={1}>
                            <Icon name="Calculator" />
                        </Box>
                        <Typography color="dark">{t('profile:feeScale')}</Typography>
                    </Box>
                    <ExternalLink
                        href={String(user.feeScaleFile)}
                        target="_blank"
                        rel="noreferrer noopener"
                        onClick={(ev) => {
                            ev.preventDefault();
                            openFeeScaleModal();
                        }}
                    >
                        {t('profile:seeFeeScale')}
                    </ExternalLink>
                    {feeScaleModalProps.open && (
                        <FeeScaleModal
                            feeScaleFile={user.feeScaleFile}
                            feeScaleFileName={user.feeScaleFileName}
                            {...feeScaleModalProps}
                        />
                    )}
                </Box>
            ) : (
                company?.feeScaleFile &&
                company.feeScaleFileName && (
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent={displayOnCard ? 'space-between' : 'flex-start'}
                        my={displayOnCard ? 2 : 1.5}
                        data-target-id="CompanyDetails-feeScaleFile-company"
                    >
                        <Box display="flex" alignItems="center" mr={2} minWidth={isMobile ? '30%' : '40%'}>
                            <Box display="flex" alignItems="center" mr={1}>
                                <Icon name="Calculator" />
                            </Box>
                            <Typography color="dark">{t('profile:feeScale')}</Typography>
                        </Box>
                        <ExternalLink
                            href={String(company.feeScaleFile)}
                            target="_blank"
                            rel="noreferrer noopener"
                            onClick={(ev) => {
                                ev.preventDefault();
                                openFeeScaleModal();
                            }}
                        >
                            {t('profile:seeFeeScale')}
                        </ExternalLink>
                        {feeScaleModalProps.open && (
                            <FeeScaleModal
                                feeScaleFile={company.feeScaleFile}
                                feeScaleFileName={company.feeScaleFileName}
                                {...feeScaleModalProps}
                            />
                        )}
                    </Box>
                )
            )}
            {company?.contactUser?.id && displayOnCard && company.contactUser.id !== currentUser?.id && (
                <ChatButton userId={company.contactUser.id} data-target-id="CompanyDetails-chat" fullWidth />
            )}
        </>
    );

    return displayOnCard ? (
        <StyledCard>
            <StyledCardContent>{content}</StyledCardContent>
        </StyledCard>
    ) : (
        <Box display="flex" flexDirection="column">
            {content}
        </Box>
    );
};

export default CompanyDetailsBlock;
